import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError, timer } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { FinancePaymentsApi } from '../api/finance-payments.api';
import { ApiResponse, PagedResult } from '../models/api.models';
import {
  ApprovalQuery,
  ApprovalRequest,
  DDTransaction,
  DirectDebitMandate,
  InitiatePaymentResponse,
  PaymentOutcome,
  PaymentsOverview,
  TransactionQuery,
} from '../models/finance.models';

/** How often the header checks for new payment requests. */
const REQUEST_POLL_MS = 60_000;

@Injectable({ providedIn: 'root' })
export class FinancePaymentsDataAccess {
  private api = inject(FinancePaymentsApi);

  /** Linked direct debit account, or null when none is set up. */
  /**
   * Linked direct debit account, or null when none is set up (404).
   * Read the same way as the admin app: the account may come inside `data` or
   * at the top level of the response. Other errors (e.g. 403) are thrown so
   * the screen can say it couldn't check, instead of "no account".
   */
  getMandate(): Observable<DirectDebitMandate | null> {
    return this.api.getMandate().pipe(
      map(toMandate),
      catchError((err: HttpErrorResponse) => (err.status === 404 ? of(null) : throwError(() => err))),
    );
  }

  /**
   * The account for the "Account Linked to Direct Debit" card, loaded the way
   * the admin Payments page does it: POST /DirectDebit/refresh-status first
   * (latest status from SecurePay), falling back to GET /DirectDebit when the
   * refresh fails or returns nothing.
   */
  loadMandate(): Observable<DirectDebitMandate | null> {
    return this.api.refreshMandateStatus().pipe(
      map(toMandate),
      catchError(() => of(null)),
      switchMap((mandate) => (mandate ? of(mandate) : this.getMandate())),
    );
  }

  getOverview(range: { fromDate?: string; toDate?: string } = {}): Observable<PaymentsOverview> {
    return this.api.getOverview(range).pipe(map((res) => res.data));
  }

  /** Requests to pay or reject, oldest first (includes failed ones that can be retried). */
  getApprovalRequests(query: ApprovalQuery = {}): Observable<ApprovalRequest[]> {
    return this.api
      .getPendingApproval({ pageNumber: 1, pageSize: 100, ...query })
      .pipe(map((res) => res.data?.items ?? []));
  }

  /**
   * Requests, re-checked every minute while subscribed (used by the header bell).
   * Shared, so the header and any other subscriber cause one request per check.
   * Errors are swallowed so a failed check doesn't stop the next one.
   */
  readonly approvalRequests$: Observable<ApprovalRequest[]> = timer(0, REQUEST_POLL_MS).pipe(
    switchMap(() => this.getApprovalRequests().pipe(catchError(() => of<ApprovalRequest[] | null>(null)))),
    map((requests) => requests ?? []),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  exportApprovalRequests(format: 'csv' | 'pdf', query: ApprovalQuery = {}): Observable<Blob> {
    return this.api.exportPendingApproval(format, query);
  }

  rejectRequest(payoutId: string, reason: string): Observable<unknown> {
    return this.api.reject(payoutId, reason);
  }

  /** Starts a payment: returns the fee and emails an OTP. Nothing is debited yet. */
  initiatePayment(payoutId: string): Observable<InitiatePaymentResponse> {
    return this.api.initiatePayment(payoutId).pipe(map(requireData));
  }

  /**
   * Confirms the OTP and turns the response into one outcome:
   *   200 → paid · 202 → processing · 400 with failureReason → failed.
   * Wrong/expired OTP (400 without failureReason), 403 and 409 are thrown so
   * the modal can show the message and let the user fix it.
   */
  confirmPayment(payoutId: string, otp: string): Observable<PaymentOutcome> {
    return this.api.confirmPayment(payoutId, otp).pipe(
      map((response): PaymentOutcome => {
        if (response.status === 202) {
          return { kind: 'processing', message: response.body?.message || 'The payment is with the bank.' };
        }
        return { kind: 'paid', payment: requireData(response.body!) };
      }),
      catchError((err: HttpErrorResponse) => {
        const failureReason = err.error?.data?.failureReason;
        if (err.status === 400 && failureReason) return of<PaymentOutcome>({ kind: 'failed', reason: failureReason });
        return throwError(() => err);
      }),
    );
  }

  getTransactions(query: TransactionQuery): Observable<PagedResult<DDTransaction>> {
    return this.api.getTransactions(query).pipe(map((res) => res.data));
  }

  exportTransactions(format: 'csv' | 'pdf', status?: string): Observable<Blob> {
    return this.api.exportTransactions(format, status);
  }
}

/**
 * Reads an account from a /DirectDebit response the same way the admin app
 * does: inside `data` or at the top level. Null when it isn't an account.
 */
function toMandate(res: any): DirectDebitMandate | null {
  const data = res?.data ?? res;
  const looksLikeMandate = !!data && (typeof data.status === 'string' || !!data.accountNumber);
  return looksLikeMandate ? (data as DirectDebitMandate) : null;
}

/** Unwraps `data`, or fails with the API's message when it's missing. */
function requireData<T>(res: ApiResponse<T>): T {
  if (res?.data == null) throw { error: { message: res?.message || 'Unexpected response from the server.' } };
  return res.data;
}
